import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Tabs, Tab, Typography, Button, Paper, Grid, TextField,
  CircularProgress, Alert, Divider, Chip, Switch, IconButton,
  Tooltip, Table, TableHead, TableBody, TableRow, TableCell,
  TableContainer, Dialog, DialogTitle, DialogContent, DialogActions,
  Avatar, Skeleton,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Store as StoreIcon,
  People as PeopleIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  PersonAdd as PersonAddIcon,
  Close as CloseIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import {
  updateCompany,
  deleteCompany as deleteCompanyApi,
  getAllStores,
  createStore,
  updateStore,
  deleteStore,
  activateStore,
  deactivateStore,
  enableUser,
  disableUser,
  deleteUser,
} from '../services/adminUserService';
import UserListTable from './UserListTable';
import CreateUserForm from './CreateUserForm';
import StoreDialog from './StoreDialog';
import EditUserDialog from './EditUserDialog';
import PasswordDialog from './PasswordDialog';

// ── Company info fields ─────────────────────────────────────────────────────
const COMPANY_FIELDS = [
  { name: 'name',            label: 'Company Name',     required: true, grid: 12 },
  { name: 'btwNumber',       label: 'BTW / VAT Number', grid: 6 },
  { name: 'ibaNumber',       label: 'IBAN Number',      grid: 6 },
  { name: 'bicNumber',       label: 'BIC Number',       grid: 6 },
  { name: 'website',         label: 'Website',          grid: 6 },
  { name: 'companyPeppolId', label: 'Company Peppol ID',grid: 6 },
  { name: 'street',          label: 'Street',           grid: 6 },
  { name: 'streetNumber',    label: 'Street No.',       grid: 3 },
  { name: 'postCode',        label: 'Post Code',        grid: 3 },
  { name: 'city',            label: 'City',             grid: 6 },
  { name: 'country',         label: 'Country',          grid: 6 },
];

// ── Tab panel wrapper ───────────────────────────────────────────────────────
function TabPanel({ value, index, children }) {
  return value === index ? <Box sx={{ pt: 2.5 }}>{children}</Box> : null;
}

// ── Reusable confirmation dialog ────────────────────────────────────────────
function ConfirmDialog({ open, title, message, onConfirm, onClose, loading, danger = true }) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, fontSize: '0.95rem', color: danger ? '#f44336' : 'text.primary', pb: 1 }}>
        {danger && <WarningIcon fontSize="small" />} {title}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.6 }}>{message}</Typography>
      </DialogContent>
      <Divider />
      <DialogActions sx={{ px: 3, py: 1.5, gap: 1 }}>
        <Button onClick={onClose} color="inherit" disabled={loading} sx={{ fontWeight: 600, textTransform: 'none', borderRadius: 1.5 }}>
          Cancel
        </Button>
        <Button
          onClick={onConfirm} variant="contained" disabled={loading}
          startIcon={loading ? <CircularProgress size={14} color="inherit" /> : null}
          sx={{
            fontWeight: 700, textTransform: 'none', borderRadius: 1.5,
            bgcolor: danger ? '#f44336' : '#667eea',
            '&:hover': { bgcolor: danger ? '#c62828' : '#5568d3' },
            '&.Mui-disabled': { bgcolor: '#e0e0e0' },
          }}
        >
          {loading ? 'Deleting…' : 'Confirm Delete'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ── Store table row ─────────────────────────────────────────────────────────
function StoreRow({ store, onEdit, onDelete, onToggle, toggling }) {
  const id       = store.Id        ?? store.id;
  const name     = store.StoreName ?? store.storeName  ?? 'Unnamed';
  const code     = store.StoreCode ?? store.storeCode  ?? '—';
  const isActive = store.IsActive  ?? store.isActive   ?? true;
  const phone    = store.PhoneNumber ?? store.phoneNumber ?? '—';
  const city     = store.City      ?? store.city  ?? '';
  const country  = store.Country   ?? store.country ?? '';
  const location = [city, country].filter(Boolean).join(', ') || '—';

  return (
    <TableRow hover sx={{ '&:last-child td': { borderBottom: 0 } }}>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.2 }}>
          <Avatar sx={{ width: 30, height: 30, bgcolor: '#667eea', borderRadius: 1, fontSize: '0.7rem', fontWeight: 700 }}>
            {name.charAt(0).toUpperCase()}
          </Avatar>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: '0.85rem', lineHeight: 1.2 }}>{name}</Typography>
            {code !== '—' && <Typography variant="caption" color="text.secondary">{code}</Typography>}
          </Box>
        </Box>
      </TableCell>
      <TableCell><Typography sx={{ fontSize: '0.82rem' }}>{location}</Typography></TableCell>
      <TableCell><Typography sx={{ fontSize: '0.82rem' }}>{phone}</Typography></TableCell>
      <TableCell>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
          <Switch
            size="small" checked={isActive} disabled={toggling}
            onChange={() => onToggle(store)}
            sx={{ '& .MuiSwitch-switchBase.Mui-checked': { color: '#4caf50' } }}
          />
          <Chip
            label={isActive ? 'Active' : 'Inactive'} size="small"
            sx={{
              fontWeight: 700, fontSize: '0.68rem',
              bgcolor: isActive ? 'rgba(76,175,80,0.12)' : 'rgba(158,158,158,0.12)',
              color: isActive ? '#2e7d32' : '#757575',
            }}
          />
        </Box>
      </TableCell>
      <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
        <Tooltip title="Edit store">
          <IconButton size="small" sx={{ color: '#ff9800' }} onClick={() => onEdit(store)}>
            <EditIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete store">
          <IconButton size="small" sx={{ color: '#f44336' }} onClick={() => onDelete(store)}>
            <DeleteIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Tooltip>
      </TableCell>
    </TableRow>
  );
}

// ── Main component ──────────────────────────────────────────────────────────
const CompanyDetailPanel = ({ company, onRefresh, onCompanyDeleted }) => {
  const [tab, setTab] = useState(0);

  // ── Tab 0: Company Info ─────────────────────────────────────────────────
  const [companyForm, setCompanyForm]         = useState({});
  const [savingCompany, setSavingCompany]     = useState(false);
  const [companyError, setCompanyError]       = useState('');
  const [deleteCompanyOpen, setDeleteCompanyOpen] = useState(false);
  const [deletingCompany, setDeletingCompany]     = useState(false);

  useEffect(() => {
    const init = {};
    COMPANY_FIELDS.forEach(({ name }) => { init[name] = company[name] ?? ''; });
    setCompanyForm(init);
    setCompanyError('');
  }, [company]);

  const handleSaveCompany = async () => {
    if (!companyForm.name?.trim()) { setCompanyError('Company name is required.'); return; }
    if (!company.id) { setCompanyError('Cannot save: no backend company ID was found.'); return; }
    setSavingCompany(true);
    setCompanyError('');
    try {
      await updateCompany(company.id, companyForm);
      toast.success('Company updated successfully');
      onRefresh();
    } catch (e) {
      setCompanyError(e?.response?.data?.message || e?.message || 'Update failed');
    } finally {
      setSavingCompany(false);
    }
  };

  const handleDeleteCompany = async () => {
    setDeletingCompany(true);
    try {
      await deleteCompanyApi(company.id);
      toast.success(`"${company.name}" deleted`);
      setDeleteCompanyOpen(false);
      onCompanyDeleted?.();
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || 'Delete failed');
    } finally {
      setDeletingCompany(false);
    }
  };

  // ── Tab 1: Stores ───────────────────────────────────────────────────────
  const [stores, setStores]           = useState([]);
  const [storesLoading, setStoresLoading] = useState(false);
  const [storeDialog, setStoreDialog] = useState({ open: false, mode: 'create', store: null });
  const [deletingStore, setDeletingStore] = useState(null);
  const [confirmStoreDelete, setConfirmStoreDelete] = useState(null);
  const [togglingStoreId, setTogglingStoreId] = useState(null);

  const loadStores = useCallback(async () => {
    setStoresLoading(true);
    try {
      const res = await getAllStores();
      const all = res?.payload || res?.data || res || [];
      const filtered = company.id != null
        ? all.filter(s => (s.CompanyId ?? s.companyId) === company.id)
        : all.filter(s => (s.CompanyName ?? s.companyName) === company.name);
      setStores(filtered);
    } catch {
      toast.error('Failed to load stores');
    } finally {
      setStoresLoading(false);
    }
  }, [company]);

  useEffect(() => {
    if (tab === 1) loadStores();
  }, [tab, loadStores]);

  const handleSaveStore = async (formData, storeId) => {
    if (storeId) {
      await updateStore(storeId, formData);
      toast.success('Store updated');
    } else {
      await createStore({ ...formData, CompanyId: company.id });
      toast.success('Store added');
    }
    await loadStores();
  };

  const handleDeleteStore = async () => {
    if (!confirmStoreDelete) return;
    const id = confirmStoreDelete.Id ?? confirmStoreDelete.id;
    setDeletingStore(id);
    try {
      await deleteStore(id);
      toast.success('Store deleted');
      setConfirmStoreDelete(null);
      await loadStores();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Delete failed');
    } finally {
      setDeletingStore(null);
    }
  };

  const handleToggleStore = async (store) => {
    const id = store.Id ?? store.id;
    setTogglingStoreId(id);
    try {
      const isActive = store.IsActive ?? store.isActive ?? true;
      if (isActive) {
        await deactivateStore(id);
        toast.success(`${store.StoreName ?? store.storeName} deactivated`);
      } else {
        await activateStore(id);
        toast.success(`${store.StoreName ?? store.storeName} activated`);
      }
      await loadStores();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Failed to update store status');
    } finally {
      setTogglingStoreId(null);
    }
  };

  // ── Tab 2: Users ────────────────────────────────────────────────────────
  const [editUserTarget, setEditUserTarget]       = useState(null);
  const [passwordUserTarget, setPasswordUserTarget] = useState(null);
  const [createUserOpen, setCreateUserOpen]       = useState(false);

  const usersMap = useMemo(
    () => new Map([[company.name, company.users]]),
    [company]
  );

  // Pre-fill company fields when creating a user from within the company detail
  const createUserInitials = useMemo(() => ({
    CompanyName:     company.name,
    BTWNumber:       company.btwNumber,
    IBANumber:       company.ibaNumber,
    BICNumber:       company.bicNumber,
    Website:         company.website,
    CompanyPeppolId: company.companyPeppolId,
    Street:          company.street,
    StreetNumber:    company.streetNumber,
    PostCode:        company.postCode,
    City:            company.city,
    Country:         company.country,
  }), [company]);

  const handleToggleUser = async (user, enable) => {
    try {
      const userId = user.Id ?? user.id;
      if (enable) {
        await enableUser(userId);
        toast.success(`${user.Name ?? 'User'} enabled`);
      } else {
        await disableUser(userId);
        toast.success(`${user.Name ?? 'User'} disabled`);
      }
      onRefresh();
    } catch (e) {
      toast.error(e?.response?.data?.message || 'Action failed');
    }
  };

  const handleDeleteUser = async (user) => {
    await deleteUser(user.Id ?? user.id);
    toast.success(`${user.Name ?? 'User'} deleted`);
    onRefresh();
  };

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
      {/* Tab header */}
      <Box sx={{ borderBottom: '1px solid', borderColor: 'divider', px: 2, bgcolor: 'white' }}>
        <Tabs
          value={tab} onChange={(_, v) => setTab(v)}
          textColor="primary" indicatorColor="primary"
          sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 700, fontSize: '0.87rem', minHeight: 52 } }}
        >
          <Tab icon={<BusinessIcon sx={{ fontSize: 17 }} />} iconPosition="start" label="Company Info" />
          <Tab icon={<StoreIcon sx={{ fontSize: 17 }} />} iconPosition="start" label="Stores" />
          <Tab
            icon={<PeopleIcon sx={{ fontSize: 17 }} />} iconPosition="start"
            label={
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                Users
                <Chip label={company.users.length} size="small"
                  sx={{ height: 18, fontSize: '0.65rem', fontWeight: 700 }} />
              </Box>
            }
          />
        </Tabs>
      </Box>

      <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: '#fafafa', minHeight: 400 }}>

        {/* ── TAB 0: Company Info ─────────────────────────────────────── */}
        <TabPanel value={tab} index={0}>
          {company.id == null && (
            <Alert severity="warning" sx={{ mb: 2.5, borderRadius: 1.5 }}>
              No backend company ID found — edit &amp; delete require the company to be fetched from the API.
            </Alert>
          )}
          {companyError && (
            <Alert severity="error" sx={{ mb: 2.5, borderRadius: 1.5 }} onClose={() => setCompanyError('')}>
              {companyError}
            </Alert>
          )}

          <Box sx={{ maxWidth: 740 }}>
            <Grid container spacing={2}>
              {COMPANY_FIELDS.map(({ name, label, required, grid }) => (
                <Grid item xs={12} sm={grid} key={name}>
                  <TextField
                    fullWidth size="small" label={label} required={!!required}
                    value={companyForm[name] ?? ''}
                    onChange={e => setCompanyForm(p => ({ ...p, [name]: e.target.value }))}
                    sx={{ bgcolor: 'white', '& .MuiOutlinedInput-root': { borderRadius: 1.5 } }}
                  />
                </Grid>
              ))}
            </Grid>

            <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              <Button
                variant="contained" onClick={handleSaveCompany}
                disabled={savingCompany || !company.id}
                startIcon={savingCompany ? <CircularProgress size={14} color="inherit" /> : <SaveIcon sx={{ fontSize: 16 }} />}
                sx={{
                  fontWeight: 700, textTransform: 'none', borderRadius: 1.5, px: 2.5,
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  '&:hover': { background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)' },
                  '&.Mui-disabled': { background: '#e0e0e0' },
                }}
              >
                {savingCompany ? 'Saving…' : 'Save Changes'}
              </Button>
              <Button
                variant="outlined" color="error" disabled={!company.id}
                onClick={() => setDeleteCompanyOpen(true)}
                sx={{ fontWeight: 700, textTransform: 'none', borderRadius: 1.5 }}
              >
                Delete Company
              </Button>
            </Box>
          </Box>

          <ConfirmDialog
            open={deleteCompanyOpen}
            title="Delete Company"
            message={`Permanently delete "${company.name}"? This will affect all ${company.users.length} user(s) and associated stores. This action cannot be undone.`}
            onConfirm={handleDeleteCompany}
            onClose={() => setDeleteCompanyOpen(false)}
            loading={deletingCompany}
          />
        </TabPanel>

        {/* ── TAB 1: Stores ───────────────────────────────────────────── */}
        <TabPanel value={tab} index={1}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
            <Typography variant="body2" color="text.secondary" fontWeight={600}>
              {storesLoading ? 'Loading…' : `${stores.length} store${stores.length !== 1 ? 's' : ''}`}
            </Typography>
            <Button
              variant="contained" size="small"
              startIcon={<AddIcon sx={{ fontSize: 15 }} />}
              onClick={() => setStoreDialog({ open: true, mode: 'create', store: null })}
              sx={{
                fontWeight: 700, textTransform: 'none', borderRadius: 1.5, px: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)' },
              }}
            >
              Add Store
            </Button>
          </Box>

          <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
            <TableContainer>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {['Store', 'Location', 'Phone', 'Status', 'Actions'].map((h, i) => (
                      <TableCell key={h} align={i === 4 ? 'right' : 'left'} sx={{
                        fontWeight: 700, fontSize: '0.75rem', bgcolor: '#f5f5f5',
                        textTransform: 'uppercase', letterSpacing: '0.4px', color: 'text.secondary',
                      }}>
                        {h}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {storesLoading ? (
                    [...Array(3)].map((_, i) => (
                      <TableRow key={i}>
                        {[...Array(5)].map((__, j) => (
                          <TableCell key={j}><Skeleton height={20} /></TableCell>
                        ))}
                      </TableRow>
                    ))
                  ) : stores.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} sx={{ py: 6, textAlign: 'center' }}>
                        <StoreIcon sx={{ fontSize: 40, color: 'action.disabled', display: 'block', mx: 'auto', mb: 1 }} />
                        <Typography variant="body2" color="text.secondary" fontWeight={600}>
                          No stores found for this company
                        </Typography>
                        <Button
                          size="small" sx={{ mt: 1, textTransform: 'none', fontWeight: 600 }}
                          onClick={() => setStoreDialog({ open: true, mode: 'create', store: null })}
                        >
                          Add First Store
                        </Button>
                      </TableCell>
                    </TableRow>
                  ) : stores.map(store => (
                    <StoreRow
                      key={store.Id ?? store.id}
                      store={store}
                      toggling={togglingStoreId === (store.Id ?? store.id)}
                      onEdit={s => setStoreDialog({ open: true, mode: 'edit', store: s })}
                      onDelete={s => setConfirmStoreDelete(s)}
                      onToggle={handleToggleStore}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <StoreDialog
            open={storeDialog.open}
            mode={storeDialog.mode}
            store={storeDialog.store}
            onClose={() => setStoreDialog(s => ({ ...s, open: false }))}
            onSave={handleSaveStore}
          />

          <ConfirmDialog
            open={!!confirmStoreDelete}
            title="Delete Store"
            message={`Delete "${confirmStoreDelete?.StoreName ?? confirmStoreDelete?.storeName}"? This cannot be undone.`}
            onConfirm={handleDeleteStore}
            onClose={() => setConfirmStoreDelete(null)}
            loading={!!deletingStore}
          />
        </TabPanel>

        {/* ── TAB 2: Users ────────────────────────────────────────────── */}
        <TabPanel value={tab} index={2}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon sx={{ fontSize: 17 }} />}
              onClick={() => setCreateUserOpen(true)}
              sx={{
                fontWeight: 700, textTransform: 'none', borderRadius: 1.5, px: 2.5,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': { background: 'linear-gradient(135deg, #5568d3 0%, #6a3f8f 100%)' },
              }}
            >
              Add User
            </Button>
          </Box>

          <UserListTable
            groupedUsers={usersMap}
            loading={false}
            onEdit={setEditUserTarget}
            onPassword={setPasswordUserTarget}
            onDelete={handleDeleteUser}
            onToggleEnable={handleToggleUser}
            onRefresh={onRefresh}
          />

          {/* Create user dialog — company fields pre-filled */}
          <Dialog
            open={createUserOpen}
            onClose={(_, reason) => { if (reason !== 'backdropClick' && reason !== 'escapeKeyDown') setCreateUserOpen(false); }}
            disableEscapeKeyDown
            maxWidth="md" fullWidth
            PaperProps={{ sx: { borderRadius: 2.5, maxHeight: '92vh' } }}
          >
            <DialogTitle sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <PersonAddIcon sx={{ color: '#667eea', fontSize: 22 }} />
                  <Box>
                    <Typography sx={{ fontWeight: 800, lineHeight: 1.2 }}>
                      Add User to {company.name}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Company details are pre-filled
                    </Typography>
                  </Box>
                </Box>
                <IconButton size="small" onClick={() => setCreateUserOpen(false)}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ pt: 3 }}>
              <CreateUserForm
                onCreated={() => { setCreateUserOpen(false); onRefresh(); }}
                initialValues={createUserInitials}
              />
            </DialogContent>
          </Dialog>

          <EditUserDialog
            open={!!editUserTarget}
            user={editUserTarget}
            onClose={() => setEditUserTarget(null)}
            onUpdated={onRefresh}
          />
          <PasswordDialog
            open={!!passwordUserTarget}
            user={passwordUserTarget}
            onClose={() => setPasswordUserTarget(null)}
          />
        </TabPanel>

      </Box>
    </Paper>
  );
};

export default CompanyDetailPanel;
